import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, Trash2, Loader2, Pencil, KeyRound, Save, Upload, X, Image as ImageIcon, Power, PowerOff, Eye, EyeOff } from "lucide-react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const inp = "bg-muted/40 border border-border rounded-lg px-4 py-2.5 text-sm text-foreground focus:outline-none focus:border-primary/50";

const ROLE_OPTIONS = [
  { value: "atendimento", label: "Atendimento" },
  { value: "consultor",   label: "Consultor de Cuidados" },
  { value: "gestor",      label: "Gestor de Cliente" },
  { value: "dt",          label: "Direção Técnica" },
  { value: "direcao",     label: "Direção de Rede" },
  { value: "dgo",         label: "DGO" },
  { value: "recrutamento", label: "Recrutamento" },
  { value: "admin",       label: "Admin" },
];

export default function UsersSettings() {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "atendimento", office: "" });
  const [busy, setBusy] = useState(false);
  const [editUser, setEditUser] = useState(null); // user being edited (opens dialog)
  const [editDraft, setEditDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showInactive, setShowInactive] = useState(false);   // filter toggle
  const [deleteTarget, setDeleteTarget] = useState(null);    // {user, softInfo?}
  const [deleteBusy, setDeleteBusy] = useState(false);

  const load = () => api.get("/users").then(r => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.email || !form.password || !form.name) { toast.error("Preencha todos os campos"); return; }
    setBusy(true);
    try {
      await api.post("/users", form);
      toast.success("Utilizador criado");
      setForm({ email: "", name: "", password: "", role: "atendimento", office: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    } finally { setBusy(false); }
  };

  const toggleActive = async (u) => {
    const next = u.active === false;   // if inactive, activate; else deactivate
    if (!next && !window.confirm(`Desactivar ${u.name}? Não vai conseguir fazer login enquanto estiver inactivo.`)) return;
    try {
      await api.patch(`/users/${u.id}`, { active: next });
      toast.success(next ? "Utilizador reactivado" : "Utilizador desactivado");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    }
  };

  const askDelete = (u) => setDeleteTarget({ user: u, softInfo: null, force: false });

  const doDelete = async (force = false) => {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    try {
      await api.delete(`/users/${deleteTarget.user.id}${force ? "?force=true" : ""}`);
      toast.success(force ? "Utilizador eliminado" : "Utilizador removido");
      setDeleteTarget(null);
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      // Backend soft-deletes and returns 409 with soft_deleted:true when
      // the user has references — surface the message and let admin
      // decide whether to force hard-delete.
      if (typeof detail === "object" && detail?.soft_deleted) {
        toast.info(`Utilizador desactivado — ${detail.ref_count} lead(s) referenciam-no.`);
        setDeleteTarget({
          user: deleteTarget.user,
          softInfo: detail,
        });
        load();  // refresh so active=false shows
      } else {
        toast.error(typeof detail === "string" ? detail : (detail?.message || "Erro"));
      }
    } finally { setDeleteBusy(false); }
  };

  const updateStaffId = async (id, staffId) => {
    try { await api.patch(`/users/${id}`, { zoho_staff_id: staffId }); toast.success("Staff ID atualizado"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const openEdit = (u) => {
    setEditUser(u);
    setEditDraft({
      name: u.name || "",
      email: u.email || "",
      role: u.role || "atendimento",
      title: u.title || "",
      phone: u.phone || "",
      zoho_staff_id: u.zoho_staff_id || "",
      clickup_user_id: u.clickup_user_id || "",
      linkus_extension: u.linkus_extension || "",
      office: u.office || "",
      also_dt: !!u.also_dt,
      // 25/02/2026 — Default true para não quebrar utilizadores
      // existentes; a admin pode desligar via a checkbox no editor.
      receives_pricing_requests: u.receives_pricing_requests !== false,
      bio: u.bio || "",
      photo_url: u.photo_url || null,
      // 26/02/2026 — Consultor de Cuidados temporário. Data (YYYY-MM-DD)
      // ou vazio. Guardamos aqui a fatia YYYY-MM-DD para o <input
      // type="date">; ao gravar convertemos para ISO end-of-day.
      temporary_consultor_until: (u.temporary_consultor_until || "").slice(0, 10),
      password: "", // empty = don't change
    });
  };

  const uploadUserPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!editUser) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post(`/users/${editUser.id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setEditDraft((d) => ({ ...d, photo_url: `${data.photo_url}?t=${Date.now()}` }));
      toast.success("Foto atualizada");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao carregar foto");
    } finally {
      e.target.value = "";
    }
  };

  const removeUserPhoto = async () => {
    if (!editUser) return;
    if (!window.confirm("Remover a foto deste utilizador?")) return;
    try {
      await api.delete(`/users/${editUser.id}/photo`);
      setEditDraft((d) => ({ ...d, photo_url: null }));
      toast.success("Foto removida");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro");
    }
  };

  const saveEdit = async () => {
    if (!editDraft.name.trim() || !editDraft.email.trim()) { toast.error("Nome e email são obrigatórios"); return; }
    setSaving(true);
    try {
      const payload = { ...editDraft };
      if (!payload.password) delete payload.password;
      // photo_url is managed via dedicated /photo endpoints — don't overwrite the server-side URL
      delete payload.photo_url;
      // 26/02/2026 — Consultor temporário: converte YYYY-MM-DD para ISO
      // end-of-day (23:59:59Z) para o utilizador ficar visível no
      // dropdown até ao fim do dia escolhido. Se admin apagou o campo,
      // envia string vazia — o backend normaliza para None (revoga).
      const tcu = (payload.temporary_consultor_until || "").trim();
      if (!tcu) {
        payload.temporary_consultor_until = "";
      } else if (/^\d{4}-\d{2}-\d{2}$/.test(tcu)) {
        payload.temporary_consultor_until = `${tcu}T23:59:59+00:00`;
      }
      await api.patch(`/users/${editUser.id}`, payload);
      toast.success("Utilizador atualizado");
      setEditUser(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    } finally { setSaving(false); }
  };

  return (
    <div className="p-6 lg:p-8 max-w-[1400px] mx-auto space-y-6" data-testid="users-settings-root">
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-semibold mb-2">Administração</div>
        <h1 className="font-display text-4xl lg:text-5xl tracking-tight">Utilizadores</h1>
      </div>

      {/* Create */}
      <div className="bg-card border border-border rounded-2xl p-6 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <input data-testid="new-user-name" placeholder="Nome" className={inp} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
          <input data-testid="new-user-email" placeholder="Email" type="email" className={inp} value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
          <input data-testid="new-user-password" placeholder="Password" type="password" className={inp} value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} />
          <select data-testid="new-user-role" className={inp} value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}>
            {ROLE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <button data-testid="new-user-btn" onClick={create} disabled={busy} className="bg-foreground hover:bg-slate-800 text-background font-semibold rounded-lg px-4 py-2.5 text-sm inline-flex items-center justify-center gap-2">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />} Adicionar
          </button>
        </div>
        {/* 05/02/2026 — Office só faz sentido para roles com escritório
            (DT, Direção, Gestor, Consultor, Recrutamento). Não polui outros. */}
        {["dt","direcao","gestor","consultor","recrutamento"].includes(form.role) && (
          <input
            data-testid="new-user-office"
            placeholder="Escritório (ex: Porto, Lisboa, Braga…) — crítico para atribuir DT por office"
            className={inp}
            value={form.office}
            onChange={e => setForm({ ...form, office: e.target.value })}
          />
        )}
      </div>

      {/* Table + filter */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          {users.filter(u => showInactive || u.active !== false).length} utilizador(es)
          {!showInactive && users.some(u => u.active === false) && (
            <span className="ml-2 text-xs text-muted-foreground/70">
              ({users.filter(u => u.active === false).length} inactivos escondidos)
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={() => setShowInactive(v => !v)}
          data-testid="toggle-show-inactive"
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-border text-xs text-muted-foreground hover:bg-muted"
        >
          {showInactive ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
          {showInactive ? "Esconder inactivos" : "Mostrar inactivos"}
        </button>
      </div>
      <div className="bg-card border border-border rounded-2xl overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">
              <th className="text-left px-4 py-3">Nome</th>
              <th className="text-left px-4 py-3">Email</th>
              <th className="text-left px-4 py-3">Papel</th>
              <th className="text-left px-4 py-3">Estado</th>
              <th className="text-left px-4 py-3">Zoho Staff ID</th>
              <th className="text-right px-4 py-3">Acções</th>
            </tr>
          </thead>
          <tbody>
            {users
              .filter(u => showInactive || u.active !== false)
              .map(u => {
                const isInactive = u.active === false;
                return (
              <tr
                key={u.id}
                className={`border-t border-border ${isInactive ? "opacity-55 bg-muted/40" : "hover:bg-muted/40"}`}
                data-testid={`user-row-${u.id}`}
              >
                <td className="px-4 py-2.5 text-sm font-medium">{u.name}</td>
                <td className="px-4 py-2.5 text-sm text-muted-foreground">{u.email}</td>
                <td className="px-4 py-2.5 text-xs">
                  <span className="inline-flex uppercase tracking-[0.15em] px-2 py-0.5 rounded-full border border-border text-muted-foreground bg-muted/40">{u.role}</span>
                  {/* 26/02/2026 — Badge extra quando o utilizador tem
                      um Consultor Temporário activo (data futura). */}
                  {u.is_consultor_effective === true && u.role !== "consultor" && u.temporary_consultor_until && (
                    <span
                      className="ml-2 inline-flex uppercase tracking-[0.14em] px-2 py-0.5 rounded-full bg-[hsl(var(--info-bg))] text-[hsl(var(--info))] text-[10px] font-semibold border border-[hsl(var(--info))]/25"
                      data-testid={`user-temp-consultor-${u.id}`}
                      title={`Consultor temporário até ${new Date(u.temporary_consultor_until).toLocaleDateString("pt-PT")}`}
                    >
                      Consultor temp.
                    </span>
                  )}
                </td>
                <td className="px-4 py-2.5 text-xs">
                  {isInactive ? (
                    <span className="shift-chip shift-chip-danger" data-testid={`user-status-${u.id}`}>Inactivo</span>
                  ) : (
                    <span className="shift-chip shift-chip-sage" data-testid={`user-status-${u.id}`}>Activo</span>
                  )}
                </td>
                <td className="px-4 py-2.5 text-xs">
                  {(u.role === "gestor" || u.role === "consultor" || u.role === "admin") ? (
                    <input
                      data-testid={`user-staff-${u.id}`}
                      defaultValue={u.zoho_staff_id || ""}
                      placeholder="ex: 4000000123456"
                      onBlur={e => { const v = e.target.value.trim(); if (v !== (u.zoho_staff_id || "")) updateStaffId(u.id, v); }}
                      className="bg-muted/40 border border-border rounded px-2 py-1 text-xs w-36"
                      disabled={isInactive}
                    />
                  ) : <span className="text-muted-foreground/70">—</span>}
                </td>
                <td className="px-4 py-2.5">
                  <div className="flex items-center justify-end gap-1.5 flex-nowrap">
                    <button
                      onClick={() => openEdit(u)}
                      data-testid={`edit-user-${u.id}`}
                      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.12em] px-2.5 py-1.5 rounded-full border border-border text-muted-foreground hover:border-primary hover:text-foreground whitespace-nowrap"
                      title="Editar utilizador"
                    ><Pencil className="w-3 h-3" /> Editar</button>
                    <button
                      onClick={() => toggleActive(u)}
                      data-testid={`toggle-active-${u.id}`}
                      className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.12em] px-2.5 py-1.5 rounded-full border whitespace-nowrap ${
                        isInactive
                          ? "border-[hsl(var(--success))]/50 text-[hsl(var(--success))] hover:bg-[hsl(var(--success-bg))]"
                          : "border-[hsl(var(--warning))]/40 text-[hsl(var(--warning))] hover:bg-[hsl(var(--warning-bg))]"
                      }`}
                      title={isInactive ? "Reactivar utilizador" : "Desactivar utilizador (login bloqueado)"}
                    >
                      {isInactive ? <Power className="w-3 h-3" /> : <PowerOff className="w-3 h-3" />}
                      {isInactive ? "Reactivar" : "Desactivar"}
                    </button>
                    <button
                      onClick={() => askDelete(u)}
                      data-testid={`delete-user-${u.id}`}
                      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.12em] px-2.5 py-1.5 rounded-full border border-[hsl(var(--danger))]/30 text-[hsl(var(--danger))] hover:bg-[hsl(var(--danger))]/10 whitespace-nowrap"
                      title="Eliminar utilizador (permanente ou desactivação se tiver leads)"
                    ><Trash2 className="w-3 h-3" /> Eliminar</button>
                  </div>
                </td>
              </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      {/* Delete confirmation dialog — handles soft-delete cascade + force option */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => { if (!open && !deleteBusy) setDeleteTarget(null); }}>
        <DialogContent className="max-w-md" data-testid="delete-user-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {deleteTarget?.softInfo ? "Confirmar eliminação definitiva" : "Eliminar utilizador"}
            </DialogTitle>
            <DialogDescription>
              {deleteTarget?.softInfo ? (
                <>O utilizador <b>{deleteTarget?.user?.name}</b> já foi <b>desactivado</b>. Como está associado a <b>{deleteTarget.softInfo.ref_count} lead(s)</b>, uma eliminação definitiva vai deixar essas referências órfãs (ficam com o ID mas sem nome). Continuar mesmo assim?</>
              ) : (
                <>Vai eliminar o utilizador <b>{deleteTarget?.user?.name}</b> ({deleteTarget?.user?.email}). Se estiver associado a leads, o sistema desactiva-o em vez de eliminar (login bloqueado, referências preservadas).</>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <button
              onClick={() => setDeleteTarget(null)}
              disabled={deleteBusy}
              className="px-4 py-2 rounded-lg border border-border text-sm text-muted-foreground hover:bg-muted disabled:opacity-60"
              data-testid="delete-user-cancel"
            >Cancelar</button>
            <button
              onClick={() => doDelete(!!deleteTarget?.softInfo)}
              disabled={deleteBusy}
              className={`px-5 py-2 rounded-lg text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60 ${
                deleteTarget?.softInfo
                  ? "bg-[hsl(var(--danger))] hover:bg-[hsl(var(--danger))]/85 text-white"
                  : "bg-foreground hover:bg-foreground/85 text-background"
              }`}
              data-testid="delete-user-confirm"
            >
              {deleteBusy && <Loader2 className="w-4 h-4 animate-spin" />}
              {deleteTarget?.softInfo ? "Eliminar definitivamente" : "Eliminar"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editUser} onOpenChange={(open) => { if (!open) setEditUser(null); }}>
        <DialogContent className="max-w-lg bg-card" data-testid="edit-user-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl inline-flex items-center gap-2">
              <Pencil className="w-5 h-5 text-foreground" /> Editar utilizador
            </DialogTitle>
            <DialogDescription>Altera os dados abaixo. Deixa a password em branco para não alterar.</DialogDescription>
          </DialogHeader>

          {editDraft && (
            <div className="space-y-4">
              <div>
                <label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold block mb-1">Nome *</label>
                <input data-testid="edit-user-name" className={`${inp} w-full`} value={editDraft.name} onChange={e => setEditDraft({ ...editDraft, name: e.target.value })} />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold block mb-1">Email *</label>
                <input data-testid="edit-user-email" type="email" className={`${inp} w-full`} value={editDraft.email} onChange={e => setEditDraft({ ...editDraft, email: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold block mb-1">Papel</label>
                  <select data-testid="edit-user-role" className={`${inp} w-full`} value={editDraft.role} onChange={e => setEditDraft({ ...editDraft, role: e.target.value })}>
                    {ROLE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold block mb-1">
                    Escritório
                    {!["dt","direcao","gestor","consultor","recrutamento"].includes(editDraft.role) && (
                      <span className="ml-1 text-muted-foreground/60 normal-case tracking-normal font-normal">(opcional)</span>
                    )}
                  </label>
                  <input
                    data-testid="edit-user-office"
                    className={`${inp} w-full`}
                    value={editDraft.office || ""}
                    onChange={e => setEditDraft({ ...editDraft, office: e.target.value })}
                    placeholder="Ex: Porto, Lisboa, Braga…"
                  />
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold block mb-1">Telefone</label>
                <input data-testid="edit-user-phone" className={`${inp} w-full`} value={editDraft.phone} onChange={e => setEditDraft({ ...editDraft, phone: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold block mb-1">Cargo</label>
                  <input data-testid="edit-user-title" className={`${inp} w-full`} value={editDraft.title} onChange={e => setEditDraft({ ...editDraft, title: e.target.value })} placeholder="Ex: Gestora Sénior de Clientes" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold block mb-1">Zoho Staff ID</label>
                  <input data-testid="edit-user-staff" className={`${inp} w-full`} value={editDraft.zoho_staff_id} onChange={e => setEditDraft({ ...editDraft, zoho_staff_id: e.target.value })} placeholder="ex: 4000000123456" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold block mb-1">ClickUp User ID <span className="text-muted-foreground normal-case tracking-normal">(para @-mentions)</span></label>
                  <input data-testid="edit-user-clickup" className={`${inp} w-full`} value={editDraft.clickup_user_id} onChange={e => setEditDraft({ ...editDraft, clickup_user_id: e.target.value })} placeholder="ex: 106673245" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold block mb-1">Extensão Linkus <span className="text-muted-foreground normal-case tracking-normal">(PBX)</span></label>
                  <input data-testid="edit-user-linkus-ext" className={`${inp} w-full`} value={editDraft.linkus_extension} onChange={e => setEditDraft({ ...editDraft, linkus_extension: e.target.value })} placeholder="ex: 101" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div></div>
                <div>
                  {/* also_dt flag retro-compat — mantido apenas para
                      utilizadores migrados do papel TQA. Ver models.py. */}
                </div>
              </div>
              {/* 25/02/2026 — Opt-in/out para pedidos de valorização Pontual.
                  Só faz sentido em roles que participam do fan-out email
                  (admin + consultor). Fora desses, esconde para reduzir
                  ruído no formulário. */}
              {(editDraft.role === "admin" || editDraft.role === "consultor") && (
                <label
                  className="flex items-start gap-2 cursor-pointer bg-[hsl(var(--info-bg))] border border-[#C7D2FE] rounded-lg p-3"
                  data-testid="edit-user-receives-pricing-label"
                >
                  <input
                    type="checkbox"
                    data-testid="edit-user-receives-pricing"
                    checked={!!editDraft.receives_pricing_requests}
                    onChange={e => setEditDraft({ ...editDraft, receives_pricing_requests: e.target.checked })}
                    className="mt-0.5 w-4 h-4 accent-[#1D4ED8] cursor-pointer flex-shrink-0"
                  />
                  <span className="text-xs leading-relaxed text-[#3730A3]">
                    <b>Recebe pedidos de valorização Pontual</b><br />
                    <span className="text-[10px] text-muted-foreground">Desliga se este utilizador não deve receber os emails de fan-out ("[Caring] Pontual à espera de valorização"). Útil para contas placeholder sem mailbox real.</span>
                  </span>
                </label>
              )}
              {/* 26/02/2026 — Consultor de Cuidados temporário. Só admin
                  (o PATCH tem require_roles("admin")). A checkbox + data
                  aparece para qualquer role que NÃO seja consultor
                  (para consultores reais o campo é irrelevante).
                  Ao gravar, YYYY-MM-DD converte-se para ISO end-of-day
                  no `saveEdit` para o utilizador ficar visível até ao
                  fim do dia escolhido. */}
              {editDraft.role !== "consultor" && (
                <label
                  className="flex items-start gap-2 cursor-pointer bg-[hsl(var(--info-bg))] border border-[hsl(var(--info))]/30 rounded-lg p-3"
                  data-testid="edit-user-temp-consultor-label"
                >
                  <input
                    type="checkbox"
                    data-testid="edit-user-temp-consultor-toggle"
                    checked={!!editDraft.temporary_consultor_until}
                    onChange={e => {
                      if (e.target.checked) {
                        // default: 30 dias a partir de hoje
                        const d = new Date();
                        d.setDate(d.getDate() + 30);
                        const iso = d.toISOString().slice(0, 10);
                        setEditDraft({ ...editDraft, temporary_consultor_until: iso });
                      } else {
                        setEditDraft({ ...editDraft, temporary_consultor_until: "" });
                      }
                    }}
                    className="mt-0.5 w-4 h-4 accent-[hsl(var(--info))] cursor-pointer flex-shrink-0"
                  />
                  <span className="text-xs leading-relaxed text-[hsl(var(--info))] flex-1">
                    <b>Consultor de Cuidados temporário</b><br />
                    <span className="text-[10px] text-muted-foreground">
                      Utilizador aparece no dropdown de agendamento de RTs até à data escolhida.
                      Ao expirar, deixa automaticamente de aparecer — RTs já marcadas mantêm-se com ele.
                    </span>
                    {!!editDraft.temporary_consultor_until && (
                      <div className="mt-2 flex items-center gap-2">
                        <span className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">Válido até</span>
                        <input
                          type="date"
                          data-testid="edit-user-temp-consultor-until"
                          value={editDraft.temporary_consultor_until}
                          onChange={e => setEditDraft({ ...editDraft, temporary_consultor_until: e.target.value })}
                          min={new Date().toISOString().slice(0, 10)}
                          className={`${inp} text-xs`}
                        />
                      </div>
                    )}
                  </span>
                </label>
              )}
              {/* Manager profile: photo + bio (shown on the client proposal via the gestor_card block) */}
              <div className="border-t border-border pt-4 mt-2">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--warning))] font-semibold mb-3">Perfil do gestor (aparece na proposta ao cliente)</div>
                <div className="flex items-start gap-4 mb-3">
                  <div className="w-20 h-20 rounded-full border border-border bg-muted/40 overflow-hidden flex items-center justify-center flex-shrink-0" data-testid="edit-user-photo-preview">
                    {editDraft.photo_url ? (
                      <img src={editDraft.photo_url.startsWith("http") ? editDraft.photo_url : `${process.env.REACT_APP_BACKEND_URL}${editDraft.photo_url}`} alt="Foto" className="w-full h-full object-cover" />
                    ) : (
                      <ImageIcon className="w-6 h-6 text-muted-foreground/70" />
                    )}
                  </div>
                  <div className="flex-1 space-y-2">
                    <label className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-primary hover:bg-primary text-primary-foreground text-xs font-semibold cursor-pointer" data-testid="edit-user-photo-upload-label">
                      <Upload className="w-3.5 h-3.5" />
                      <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={uploadUserPhoto} data-testid="edit-user-photo-upload" />
                      {editDraft.photo_url ? "Substituir foto" : "Carregar foto"}
                    </label>
                    {editDraft.photo_url && (
                      <button onClick={removeUserPhoto} className="ml-2 inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-border text-xs text-muted-foreground hover:bg-muted" data-testid="edit-user-photo-remove">
                        <X className="w-3.5 h-3.5" /> Remover
                      </button>
                    )}
                    <div className="text-[10px] text-muted-foreground">JPG, PNG ou WebP · máx 5MB</div>
                  </div>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold block mb-1">Bio (aparece no bloco "O seu gestor dedicado")</label>
                  <textarea data-testid="edit-user-bio" rows={4} className={`${inp} w-full resize-y`} value={editDraft.bio} onChange={e => setEditDraft({ ...editDraft, bio: e.target.value })} maxLength={800} placeholder="Ex: Sou a Ana Ribeiro, Gestora Sénior de Clientes na Caring há 12 anos…" />
                  <div className="text-[10px] text-muted-foreground/70 mt-1 text-right">{(editDraft.bio || "").length}/800</div>
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold flex items-center gap-1.5 mb-1">
                  <KeyRound className="w-3 h-3" /> Nova password (deixar vazio para manter)
                </label>
                <input data-testid="edit-user-password" type="password" className={`${inp} w-full`} value={editDraft.password} onChange={e => setEditDraft({ ...editDraft, password: e.target.value })} placeholder="••••••••" />
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <button onClick={() => setEditUser(null)} className="px-4 py-2 rounded-lg border border-border text-sm text-muted-foreground hover:bg-muted" data-testid="edit-user-cancel">Cancelar</button>
            <button
              onClick={saveEdit}
              disabled={saving}
              className="px-5 py-2 rounded-lg bg-foreground hover:bg-slate-800 text-background text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60"
              data-testid="edit-user-save"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
